import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import B2BLayout from '../../../layouts/b2b'
import {
  Space,
  Button,
  Modal,
  Form,
  DatePicker,
  Card,
  Statistic,
  Skeleton,
  Result,
  Checkbox,
  Pagination,
  Spin,
  Select,
} from 'antd'
import { getColumns } from './columns'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { useNavigate, useLocation } from 'react-router-dom'
import { Sorter } from '../../../interfaces'
import { objectToUrl } from '../../utils'
import { currentFiltersProps, DataType, Enums, TablePaginationPosition } from './interface'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import './index.css'
import FiltersDrawer from '../../../components/v2/filters-drawer'
import {
  getJsonSettingsByKey,
  getColumnOrder,
  TableSettingsButton,
  TransferItem,
} from '../../../components/v2/table-settings-button'
import { HStack } from 'src/react-app/ui/Stack'
import { PrintButton } from '../ui/print-button/print-button'
import { getPrintPath } from '../../../components/v2/export-excel-button'
import DateFilter from './filter-date'
import { getDataOfType } from '../../../../lib/utils/get-data-of-type'
import FixedHeader from '../../../layouts/b2b/fixed-header'
import DataTable from '../../../components/data-table'
import dayjs from 'dayjs'
import { getFilters } from './filters'
import { RouteSheetsCardModal } from './card-modal'
import { RouteSheetsStatistics } from './styles'
import { handleSaveEdit } from './actions'
import { ResultStatusType } from 'antd/lib/result'
import { formatRoundedNumber } from './utils'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { ManualRouteSheetModal } from './manual-route-sheet-modal'

const GROUP_BY_CARRIER_STORAGE_PREFIX = 'routeSheets.groupByCarrier'
const DEFAULT_CARRIER_GROUP_PAGE_SIZE = 10
const CARRIER_GROUP_PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100]
const CARRIER_GROUP_PAGE_SIZE_STORAGE_PREFIX = 'routeSheets.carrierGroupPageSize'
const NO_CARRIER_GROUP_KEY = 'without-carrier'
const NO_CARRIER_GROUP_NAME = 'Без перевозчика'
const CARRIER_GROUP_TOTAL_FIELDS = new Set([
  'distance',
  'total_items_count',
  'total_glass_units_count',
  'total_weight',
  'total_weight_fact',
  'total_volume',
  'total_delivery_amount',
  'total_shipment_amount',
  'orders_count',
  'unique_addresses_count',
  'carrier_cost',
  'margin',
  'loader_amount',
  'total_logistics_amount',
  'efficiency',
])

const getColumnDataIndex = (column: any): string | undefined => {
  const dataIndex = column.dataIndex
  if (Array.isArray(dataIndex)) return dataIndex.join('.')
  return typeof dataIndex === 'string' ? dataIndex : undefined
}

const getRouteSheetCarrierGroupKey = (record: DataType): string =>
  record.carrier_group_key || (record.carrier_id ? String(record.carrier_id) : NO_CARRIER_GROUP_KEY)

const normalizeCarrierGroupPageSize = (value: unknown): number => {
  const pageSize = Number(value)

  return CARRIER_GROUP_PAGE_SIZE_OPTIONS.includes(pageSize)
    ? pageSize
    : DEFAULT_CARRIER_GROUP_PAGE_SIZE
}

const createMenuCounters = createSelector(
  (state: any) => state.menuV2,
  counters => counters,
)

const createSession = createSelector(
  (state: any) => state.session,
  sessionData => sessionData,
)

function parseUrlSearchParams(search: string): currentFiltersProps {
  const urlParams = new URLSearchParams(search)
  return {
    date: urlParams.get('date') || undefined,
    id: Number(urlParams.get('id')) || undefined,
    vehicle_id: Number(urlParams.get('vehicle_id')) || undefined,
    carrier_id: Number(urlParams.get('carrier_id')) || undefined,
    driver_name: urlParams.get('driver_name') || undefined,
    center_name: urlParams.get('center_name') || undefined,
    zone_groups: urlParams.get('zone_groups') || undefined,
  }
}

const RouteSheetsList = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { sessionData } = useSelector(createSession)
  const userId = getDataOfType(sessionData, 'user.id', Number, null)
  const groupByCarrierStorageKey = `${GROUP_BY_CARRIER_STORAGE_PREFIX}:${userId || 'anonymous'}`
  const carrierGroupPageSizeStorageKey = `${CARRIER_GROUP_PAGE_SIZE_STORAGE_PREFIX}:${
    userId || 'anonymous'
  }`
  const isLeader = getDataOfType(sessionData, 'organization.id', Number, null) === 1
  const isManager = sessionData?.roles.includes(5)

  const [data, setData] = useState<DataType[]>([])
  const [totals, setTotals] = useState<Record<string, number>>({})
  const [availableFilters, setAvailableFilters] = useState<Enums>({
    vehicle: [],
    carrier: [],
    driver: [],
    center: [],
    zone_groups: [],
  })
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(location.search)))
  const [currentFilters, setCurrentFilters] = useState<currentFiltersProps>({})

  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const isFirstLoadCompleteRef = useRef(false)
  const [importLoading, setImportLoading] = useState(false)
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [sorter, setSorter] = useState<Sorter>({})
  const [pagination, setPagination] = useState({
    current: Number(urlParams?.page) || 1,
    pageSize: 50,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })

  const { counters } = useSelector(createMenuCounters)
  const [localCounter, setLocalCounter] = useState<null | number>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [enums, setEnums] = useState<any>({})
  const [fieldTypes, setFieldTypes] = useState<any>({})
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [selectedRouteSheet, setSelectedRouteSheet] = useState<DataType | null>(null)
  const [manualRouteSheetModalOpen, setManualRouteSheetModalOpen] = useState(false)
  const [manualRouteSheetForEdit, setManualRouteSheetForEdit] = useState<DataType | null>(null)
  const [groupByCarrier, setGroupByCarrier] = useState(
    () => localStorage.getItem(groupByCarrierStorageKey) === 'true',
  )
  const [collapsedCarrierGroups, setCollapsedCarrierGroups] = useState<Set<string>>(new Set())
  const [carrierGroupPages, setCarrierGroupPages] = useState<Record<string, number>>({})
  const [carrierGroupPageSize, setCarrierGroupPageSize] = useState(() =>
    normalizeCarrierGroupPageSize(localStorage.getItem(carrierGroupPageSizeStorageKey)),
  )
  const [loadingCarrierGroups, setLoadingCarrierGroups] = useState<Set<string>>(new Set())
  const routeSheetsListRequestIdRef = useRef(0)
  const carrierGroupRequestIdsRef = useRef<Record<string, number>>({})

  const [isModalImportVisible, setIsModalImportVisible] = useState(false)
  const [importForm] = Form.useForm()

  const headerRef = useRef<HTMLDivElement | null>(null)

  interface IError {
    code?: ResultStatusType
    message?: string
  }
  const [error, setError] = useState<null | IError>(null)

  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, location.pathname))

  useEffect(() => {
    setGroupByCarrier(localStorage.getItem(groupByCarrierStorageKey) === 'true')
    setCarrierGroupPageSize(
      normalizeCarrierGroupPageSize(localStorage.getItem(carrierGroupPageSizeStorageKey)),
    )
    setCarrierGroupPages({})
    carrierGroupRequestIdsRef.current = {}
    setLoadingCarrierGroups(new Set())
  }, [carrierGroupPageSizeStorageKey, groupByCarrierStorageKey])

  useEffect(() => {
    const newFilters = parseUrlSearchParams(location.search)
    setCurrentFilters(newFilters)
  }, [location.search])

  useEffect(() => {
    const newCounterData = counters?.['/route-sheets']
    if (newCounterData && !localCounter) {
      setLocalCounter(newCounterData)
    }
    if (newCounterData && localCounter && newCounterData !== localCounter) {
      setLocalCounter(newCounterData)
      getData()
    }
  }, [counters])

  const breadcrumbs = useMemo(() => {
    return [
      {
        href: '/route-sheets',
        title: 'Маршрутные листы',
      },
    ]
  }, [])

  const getAvailableFilters = useCallback(() => {
    fetchAPI(`/api/logistics/route-sheets/enum-list`)
      .then(data => {
        setEnums(data)

        const filters = {
          vehicle: data?.vehicle_id?.map((i: any) => ({ label: i.value, value: i.id })) || [],
          carrier: data?.carrier_id?.map((i: any) => ({ label: i.value, value: i.id })) || [],
          driver: data?.driver_name?.map((i: any) => ({ label: i.value, value: i.value })) || [],
          center: data?.center_name?.map((i: any) => ({ label: i.value, value: i.value })) || [],
          zone_groups: data?.zone_groups?.map((i: any) => ({ label: i.value, value: i.id })) || [],
        }

        setAvailableFilters(filters)
      })
      .catch(() => {
        console.error('Не удалось получить список доступных фильтров')
      })
  }, [])

  const getFieldTypes = useCallback(() => {
    fetchAPI(`/api/logistics/route-sheets/field-types`)
      .then(data => {
        setFieldTypes(data)
      })
      .catch(() => {
        console.error('Не удалось получить список доступных фильтров')
      })
  }, [])

  const getData = useCallback(() => {
    try {
      const requestId = routeSheetsListRequestIdRef.current + 1
      routeSheetsListRequestIdRef.current = requestId

      if (!isFirstLoadCompleteRef.current) {
        setLoading(true)
      } else {
        setTableLoading(true)
      }
      const urlParamsObj = Object.fromEntries(new URLSearchParams(location.search))
      if (groupByCarrier) {
        urlParamsObj.group_by_carrier = '1'
        urlParamsObj.group_page_size = String(carrierGroupPageSize)
        delete urlParamsObj.page
      }

      fetchAPI(`/api/logistics/route-sheets/list${objectToUrl(urlParamsObj)}`)
        .then(({ data, pagination, sorter, totals }) => {
          if (routeSheetsListRequestIdRef.current !== requestId) {
            return
          }

          if (data) {
            setData(
              data.map((item: any) => ({
                ...item,
              })),
            )

            pagination.hide = !!selectedDate || groupByCarrier

            setPagination(prevState => ({
              ...prevState,
              ...pagination,
            }))
            setSorter(sorter)

            if (totals) {
              console.log('totals', totals)
              setTotals(totals)
            }
          }
          if (!isFirstLoadCompleteRef.current) {
            isFirstLoadCompleteRef.current = true
          }
        })
        .catch(e => {
          if (routeSheetsListRequestIdRef.current !== requestId) {
            return
          }

          setError({
            message: (e as IHttpError)?.message || 'Неизвестная ошибка',
            code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType,
          })
        })
        .finally(() => {
          if (routeSheetsListRequestIdRef.current !== requestId) {
            return
          }

          setLoading(false)
          setTableLoading(false)
        })
    } catch (error) {
      console.error('Ошибка при получении данных:', error)
    }
  }, [carrierGroupPageSize, groupByCarrier, location.search])

  useEffect(() => {
    getAvailableFilters()
    getFieldTypes()
  }, [])

  useEffect(() => {
    getData()
  }, [getData])

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    const currentUrlParams = Object.fromEntries(new URLSearchParams(location.search))
    setPagination(prevState => ({ ...prevState, ...pagination }))

    const current = {
      ...currentUrlParams,
      sort_order: sorter?.order,
      sort_field: sorter?.field,
    } as any

    if (pagination.current) {
      current.page = pagination.current
    }
    if (pagination.pageSize) {
      current.pageSize = pagination.pageSize
    }
    if (current?.page === 1) {
      delete current.page
    }
    if (current?.sort_order === undefined) {
      delete current.sort_order
      delete current.sort_field
    }

    const urlParams = new URLSearchParams(current).toString()
    navigate({ search: urlParams })
  }

  const handleRowClick = (record: DataType) => {
    if (record.type === 'carrier-group') {
      return
    }
    setSelectedRouteSheet(record)
  }

  const handleGroupByCarrierChange = (checked: boolean) => {
    setGroupByCarrier(checked)
    localStorage.setItem(groupByCarrierStorageKey, String(checked))
    setCollapsedCarrierGroups(new Set())
    setCarrierGroupPages({})
    carrierGroupRequestIdsRef.current = {}
    setLoadingCarrierGroups(new Set())
    setSelectedRowKeys([])
  }

  const toggleCarrierGroup = useCallback((carrierKey: string) => {
    setSelectedRowKeys([])
    setCollapsedCarrierGroups(prev => {
      const next = new Set(prev)
      if (next.has(carrierKey)) {
        next.delete(carrierKey)
      } else {
        next.add(carrierKey)
      }
      return next
    })
  }, [])

  const handleCarrierGroupPageChange = useCallback(
    (carrierKey: string, page: number) => {
      setSelectedRowKeys([])

      const requestId = Date.now()
      carrierGroupRequestIdsRef.current[carrierKey] = requestId
      setLoadingCarrierGroups(prev => {
        const next = new Set(prev)
        next.add(carrierKey)
        return next
      })

      const urlParamsObj = Object.fromEntries(new URLSearchParams(location.search))
      urlParamsObj.group_by_carrier = '1'
      urlParamsObj.group_page_size = String(carrierGroupPageSize)
      urlParamsObj.carrier_group_pages = `${carrierKey}:${page}`
      urlParamsObj.carrier_group_key = carrierKey
      delete urlParamsObj.page

      fetchAPI(`/api/logistics/route-sheets/list${objectToUrl(urlParamsObj)}`)
        .then(({ data: carrierGroupData }) => {
          if (carrierGroupRequestIdsRef.current[carrierKey] !== requestId) {
            return
          }

          const rows = Array.isArray(carrierGroupData) ? carrierGroupData : []
          const carrierGroupRow = rows.find((row: DataType) => row.type === 'carrier-group')
          const routeSheetRows = rows.filter((row: DataType) => row.type !== 'carrier-group')

          setCarrierGroupPages(prev => ({
            ...prev,
            [carrierKey]: page,
          }))

          setData(prevRows => {
            const groupIndex = prevRows.findIndex(
              row => row.type === 'carrier-group' && row.carrier_group_key === carrierKey,
            )

            if (groupIndex === -1) {
              return prevRows
            }

            let nextGroupIndex = groupIndex + 1
            while (
              nextGroupIndex < prevRows.length &&
              prevRows[nextGroupIndex].type !== 'carrier-group'
            ) {
              nextGroupIndex += 1
            }

            const nextGroupRow = {
              ...prevRows[groupIndex],
              ...(carrierGroupRow || {}),
              route_sheets_page: page,
              route_sheets_page_size: carrierGroupPageSize,
            }

            return [
              ...prevRows.slice(0, groupIndex),
              nextGroupRow,
              ...routeSheetRows,
              ...prevRows.slice(nextGroupIndex),
            ]
          })
        })
        .catch(error => {
          console.error('Не удалось загрузить страницу перевозчика:', error)
        })
        .finally(() => {
          if (carrierGroupRequestIdsRef.current[carrierKey] !== requestId) {
            return
          }

          delete carrierGroupRequestIdsRef.current[carrierKey]
          setLoadingCarrierGroups(prev => {
            const next = new Set(prev)
            next.delete(carrierKey)
            return next
          })
        })
    },
    [carrierGroupPageSize, location.search],
  )

  const handleCarrierGroupPageSizeChange = useCallback(
    (pageSize: number) => {
      const normalizedPageSize = normalizeCarrierGroupPageSize(pageSize)

      setSelectedRowKeys([])
      carrierGroupRequestIdsRef.current = {}
      setLoadingCarrierGroups(new Set())
      setCarrierGroupPages({})
      setCarrierGroupPageSize(normalizedPageSize)
      localStorage.setItem(carrierGroupPageSizeStorageKey, String(normalizedPageSize))
    },
    [carrierGroupPageSizeStorageKey],
  )

  const handleModalClose = useCallback(() => {
    setSelectedRouteSheet(null)
  }, [])

  const handleOpenManualRouteSheetCreate = useCallback(() => {
    setManualRouteSheetForEdit(null)
    setManualRouteSheetModalOpen(true)
  }, [])

  const handleOpenManualRouteSheetEdit = useCallback((routeSheet: DataType) => {
    if (routeSheet.source !== 'manual') {
      return
    }

    setSelectedRouteSheet(null)
    setManualRouteSheetForEdit(routeSheet)
    setManualRouteSheetModalOpen(true)
  }, [])

  const handleCloseManualRouteSheetModal = useCallback(() => {
    setManualRouteSheetModalOpen(false)
    setManualRouteSheetForEdit(null)
  }, [])

  const handleManualRouteSheetSaved = useCallback(() => {
    handleCloseManualRouteSheetModal()
    getAvailableFilters()
    getData()
  }, [getAvailableFilters, getData, handleCloseManualRouteSheetModal])

  const onSelectChange = (selectedKeys: React.Key[]) => {
    setSelectedRowKeys(selectedKeys.filter(key => !String(key).startsWith('carrier-group:')))
  }

  const changeFiltersHandler = useCallback(
    (filters: any) => {
      const tmpFilter = { ...(filters || {}), pageSize: pagination.pageSize || 50 }
      const newUrlParams = new URLSearchParams(tmpFilter).toString()
      navigate({ search: newUrlParams })
      //getData()
    },
    [pagination.pageSize, getData, history],
  )

  const columns = useMemo(
    () => getColumns(isLeader || isManager, sorter),
    [isLeader, isManager, sorter],
  )

  const viewColumns = useMemo(() => {
    const defaultInput = { type: 'string' }

    const enhanceColumn = (column: any) => {
      if (!('dataIndex' in column) || !column.editable) {
        return column
      }

      const dataIndex = column.dataIndex

      const fieldType = fieldTypes[dataIndex]
      const input = fieldType || defaultInput

      return {
        ...column,
        input: {
          ...input,
          key: dataIndex,
        },
      }
    }

    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      const enhancedColumns = columns.map(enhanceColumn)

      const result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        const item = enhancedColumns.find(column => columnOrder[i] === column.dataIndex)
        if (item) {
          result.push(item)
        }
      }
      return result
    }

    return columns.map(enhanceColumn)
  }, [columns, jsonSettings, enums])

  const editableFields = useMemo(() => {
    return viewColumns.filter(column => column.editable).map(column => column.dataIndex as string)
  }, [viewColumns])

  const visibleData = useMemo(() => {
    if (!groupByCarrier || collapsedCarrierGroups.size === 0) {
      return data
    }

    return data.filter(record => {
      if (record.type === 'carrier-group') {
        return true
      }

      const carrierKey = getRouteSheetCarrierGroupKey(record)
      return !collapsedCarrierGroups.has(carrierKey) && !loadingCarrierGroups.has(carrierKey)
    })
  }, [collapsedCarrierGroups, data, groupByCarrier, loadingCarrierGroups])

  const carrierGroupKeys = useMemo(
    () =>
      data
        .filter(record => record.type === 'carrier-group')
        .map(record => record.carrier_group_key)
        .filter((carrierKey): carrierKey is string => Boolean(carrierKey)),
    [data],
  )

  const handleCollapseAllCarrierGroups = useCallback(() => {
    setSelectedRowKeys([])
    setCollapsedCarrierGroups(new Set(carrierGroupKeys))
  }, [carrierGroupKeys])

  const handleExpandAllCarrierGroups = useCallback(() => {
    setSelectedRowKeys([])
    setCollapsedCarrierGroups(new Set())
  }, [])

  const tableColumns = useMemo(() => {
    if (!groupByCarrier) {
      return viewColumns
    }

    const firstTotalColumnIndex = viewColumns.findIndex(column => {
      const dataIndex = getColumnDataIndex(column)
      return !!dataIndex && CARRIER_GROUP_TOTAL_FIELDS.has(dataIndex)
    })
    const firstVisibleTotalColumnIndex =
      firstTotalColumnIndex === -1 ? viewColumns.length : firstTotalColumnIndex
    const groupTitleColumnSpan =
      firstTotalColumnIndex > 0
        ? firstTotalColumnIndex + 1
        : firstTotalColumnIndex === -1
        ? viewColumns.length + 1
        : 1

    return viewColumns.map((column, columnIndex) => ({
      ...column,
      onCell: (record: DataType, rowIndex?: number) => {
        const baseCellProps =
          typeof column.onCell === 'function' ? column.onCell(record, rowIndex) || {} : {}

        if (record.type !== 'carrier-group') {
          return baseCellProps
        }

        return {
          ...baseCellProps,
          colSpan:
            columnIndex === 0
              ? groupTitleColumnSpan
              : columnIndex < firstVisibleTotalColumnIndex
              ? 0
              : undefined,
          className: [
            baseCellProps.className,
            'carrier-group-cell',
            columnIndex === 0 ? 'carrier-group-title-cell' : '',
          ]
            .filter(Boolean)
            .join(' '),
        }
      },
      render: (value: any, record: DataType, index: number) => {
        if (record.type === 'carrier-group') {
          if (columnIndex === 0) {
            const carrierKey = record.carrier_group_key
            const isCollapsed = carrierKey ? collapsedCarrierGroups.has(carrierKey) : false
            const isCarrierGroupLoading = carrierKey ? loadingCarrierGroups.has(carrierKey) : false
            const routeSheetsCount = record.route_sheets_count || 0
            const routeSheetsPage =
              (carrierKey ? carrierGroupPages[carrierKey] : undefined) ||
              record.route_sheets_page ||
              1
            const routeSheetsPageSize = record.route_sheets_page_size || carrierGroupPageSize
            const firstRouteSheetNumber =
              routeSheetsCount > 0 ? (routeSheetsPage - 1) * routeSheetsPageSize + 1 : 0
            const lastRouteSheetNumber = Math.min(
              routeSheetsPage * routeSheetsPageSize,
              routeSheetsCount,
            )

            return (
              <div className="route-sheets-carrier-group-cell">
                <button
                  type="button"
                  className="route-sheets-carrier-group"
                  onClick={event => {
                    event.stopPropagation()
                    if (carrierKey) {
                      toggleCarrierGroup(carrierKey)
                    }
                  }}
                >
                  {isCollapsed ? (
                    <RightOutlined className="route-sheets-carrier-group__icon" />
                  ) : (
                    <DownOutlined className="route-sheets-carrier-group__icon" />
                  )}
                  <span className="route-sheets-carrier-group__title">
                    {record.carrier_group_name || NO_CARRIER_GROUP_NAME}
                  </span>
                </button>
                {!isCollapsed && carrierKey && routeSheetsCount > routeSheetsPageSize && (
                  <div
                    className="route-sheets-carrier-group-pagination"
                    onClick={event => event.stopPropagation()}
                  >
                    <span className="route-sheets-carrier-group-pagination__summary">
                      {isCarrierGroupLoading ? (
                        <>
                          <Spin size="small" /> Загружаем маршруты
                        </>
                      ) : (
                        `Показано ${firstRouteSheetNumber}-${lastRouteSheetNumber} из ${routeSheetsCount}`
                      )}
                    </span>
                    <Pagination
                      current={routeSheetsPage}
                      pageSize={routeSheetsPageSize}
                      total={routeSheetsCount}
                      size="small"
                      showSizeChanger={false}
                      showLessItems
                      disabled={isCarrierGroupLoading}
                      onChange={page => handleCarrierGroupPageChange(carrierKey, page)}
                    />
                  </div>
                )}
              </div>
            )
          }

          const dataIndex = getColumnDataIndex(column)
          if (typeof dataIndex !== 'string' || !CARRIER_GROUP_TOTAL_FIELDS.has(dataIndex)) {
            return null
          }

          const groupValue = record[dataIndex as keyof DataType]
          if (groupValue === undefined || groupValue === null || groupValue === '') {
            return null
          }

          return column.render ? column.render(groupValue, record, index) : groupValue
        }

        return column.render ? column.render(value, record, index) : value
      },
    }))
  }, [
    collapsedCarrierGroups,
    carrierGroupPageSize,
    carrierGroupPages,
    groupByCarrier,
    handleCarrierGroupPageChange,
    loadingCarrierGroups,
    toggleCarrierGroup,
    viewColumns,
  ])

  const excelPath = useMemo(() => {
    return getPrintPath('/api/logistics/route-sheets/list', new URLSearchParams(location.search))
  }, [location.search])

  const settingsColumns = useMemo(() => {
    return columns.map(column => ({
      key: 'dataIndex' in column ? column.dataIndex : undefined,
      title: column.title,
    }))
  }, [columns]) as TransferItem[]

  const groupedFooterCenterContent = useMemo(() => {
    if (!groupByCarrier) {
      return undefined
    }

    return (
      <div className="route-sheets-group-page-size">
        <span>Строк в группе</span>
        <Select
          size="small"
          value={carrierGroupPageSize}
          style={{ width: 92 }}
          options={CARRIER_GROUP_PAGE_SIZE_OPTIONS.map(value => ({
            label: value,
            value,
          }))}
          onChange={handleCarrierGroupPageSizeChange}
        />
      </div>
    )
  }, [carrierGroupPageSize, groupByCarrier, handleCarrierGroupPageSizeChange])

  // Определять выбрана ли одна дата доставки в фильтре
  useEffect(() => {
    if (currentFilters?.date) {
      const [startDate, endDate] = currentFilters.date.split('-')

      if (startDate === endDate) {
        setSelectedDate(startDate)
      } else {
        setSelectedDate('')
      }
    }
  }, [currentFilters])

  // Событие изменение фильтров
  const changeDateFilterHandler = (newDate: string) => {
    setLoading(true)
    setData([])
    const urlParams = new URLSearchParams(location.search)

    if (newDate) {
      urlParams.set('date', `${newDate}-${newDate}`)
    } else {
      urlParams.delete('date')
    }

    navigate({ search: urlParams.toString() })
    setSelectedDate(newDate)
  }

  // Событие пагинации
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize: pageSize,
    }))

    const currentUrlParams = new URLSearchParams(location.search)
    currentUrlParams.set('page', page.toString())
    currentUrlParams.set('pageSize', pageSize.toString())
    navigate({ search: currentUrlParams.toString() })
  }

  const handleImportData = useCallback(
    async (values?: { date: any }) => {
      setImportLoading(true)

      const date = values ? dayjs(values.date).format('DD.MM.YYYY') : dayjs().format('DD.MM.YYYY')

      try {
        const [ryazanResponse, kalugaResponse] = await Promise.all([
          fetchAPI(`/api/logistics/route-sheets/import?date=${date}&center_name=Рязань`),
          fetchAPI(`/api/logistics/route-sheets/import?date=${date}&center_name=Калуга`),
        ])

        const allFailedOrders = [
          ...(ryazanResponse.failed_orders || []),
          ...(kalugaResponse.failed_orders || []),
        ]

        if (allFailedOrders.length > 0) {
          const errorMessages = allFailedOrders.map(order => (
            <div key={order.id}>
              <b>{`Заказ ${order.id}`}</b>
              {`: ${order.errors.join(', ')}`}
            </div>
          ))

          Modal.error({
            title: 'Ошибки при выгрузке',
            content: <div>{errorMessages}</div>,
          })
        }

        getData()
        setIsModalImportVisible(false)
        importForm.resetFields()
      } catch (error) {
        console.error('Ошибка при импорте данных:', error)
      } finally {
        setImportLoading(false)
      }
    },
    [getData, importForm],
  )

  const showImportModal = useCallback(() => {
    setIsModalImportVisible(true)
  }, [])

  const handleImportModalCancel = useCallback(() => {
    setIsModalImportVisible(false)
    importForm.resetFields()
  }, [importForm])

  const formatNumberWithSpace = (value: number | string): string => {
    return Number(value).toLocaleString('ru-RU', {
      maximumFractionDigits: 0,
    })
  }

  const clientPayments = totals.total_delivery_amount || 0
  const carrierPayments = totals.carrier_cost || 0
  const glassUnitsCount = totals.total_glass_units_count || 0
  const transportPriceItemsCount = (totals.total_items_count || 0) + glassUnitsCount
  const efficiency = carrierPayments ? clientPayments / carrierPayments : 0

  const onSaveEdit = handleSaveEdit({
    setLoading,
    getData,
    setSelectedRowKeys,
  })

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton>
      {loading && <Skeleton active />}
      {!loading && error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {!loading && !error && (
        <>
          <FixedHeader ref={headerRef}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Space size={8} wrap>
                <Checkbox
                  checked={groupByCarrier}
                  onChange={event => handleGroupByCarrierChange(event.target.checked)}
                >
                  Группировать по перевозчикам
                </Checkbox>
                {groupByCarrier && (
                  <>
                    <Button size="small" onClick={handleCollapseAllCarrierGroups}>
                      Свернуть все
                    </Button>
                    <Button size="small" onClick={handleExpandAllCarrierGroups}>
                      Развернуть все
                    </Button>
                  </>
                )}
              </Space>
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                align={'start'}
                wrap
              >
                <Space>
                  <DateFilter selectedDate={selectedDate} onChange={changeDateFilterHandler} />
                  <Button onClick={handleOpenManualRouteSheetCreate}>
                    Добавить маршрутный лист
                  </Button>
                  <Button type="primary" onClick={showImportModal} loading={importLoading}>
                    Обновить данные
                  </Button>
                </Space>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }} align={'start'}>
                  <HStack gap={'8'} align={'start'}>
                    <FiltersDrawer
                      filters={getFilters({ currentFilters, availableFilters })}
                      onChange={changeFiltersHandler}
                      currentFilters={currentFilters}
                    />
                    <PrintButton path={excelPath} columns={viewColumns} />
                    <TableSettingsButton items={settingsColumns} />
                  </HStack>
                </Space>
              </Space>
            </Space>
          </FixedHeader>

          <RouteSheetsStatistics>
            <Card style={{ marginBottom: '20px' }}>
              <Statistic
                title="Оплата клиентов"
                value={clientPayments}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    formatNumberWithSpace(value)
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Оплата перевозчикам"
                value={carrierPayments}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    formatNumberWithSpace(value)
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Маржа"
                value={(totals.total_delivery_amount || 0) - (totals.carrier_cost || 0)}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    formatNumberWithSpace(value)
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{
                  color:
                    (totals.total_delivery_amount || 0) - (totals.carrier_cost || 0) < 0
                      ? '#ff0000'
                      : '#1890ff',
                }}
              />
              <Statistic
                title="Эффективность"
                value={efficiency}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    Number(value).toLocaleString('ru-RU', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  )
                }
                valueStyle={{
                  color: efficiency < 1 ? '#ff0000' : '#1890ff',
                }}
              />
              <Statistic
                title="Километраж"
                value={totals.distance || 0}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    formatRoundedNumber(value, '')
                  )
                }
                suffix={loading ? null : 'км'}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Кол-во изделий"
                value={totals.total_items_count || 0}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    `${formatNumberWithSpace(value)}`
                  )
                }
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Кол-во стеклопакетов"
                value={glassUnitsCount}
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    `${formatNumberWithSpace(value)}`
                  )
                }
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Цена перевозки за изделие"
                value={
                  transportPriceItemsCount
                    ? ((totals.total_delivery_amount || 0) - (totals.carrier_cost || 0)) /
                      transportPriceItemsCount
                    : 0
                }
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    `${formatNumberWithSpace(value)}`
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Стоим. доставки за изделие"
                value={
                  totals.total_items_count
                    ? (totals.total_delivery_amount || 0) / (totals.total_items_count || 0)
                    : 0
                }
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    `${formatNumberWithSpace(value)}`
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{ color: '#1890ff' }}
              />
              <Statistic
                title="Стоим. перевозчика за изделие"
                value={
                  totals.total_items_count
                    ? (totals.carrier_cost || 0) / totals.total_items_count
                    : 0
                }
                precision={2}
                formatter={value =>
                  loading ? (
                    <Skeleton.Input active size="small" style={{ width: 100 }} />
                  ) : (
                    `${formatNumberWithSpace(value)}`
                  )
                }
                suffix={loading ? null : '₽'}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </RouteSheetsStatistics>
          <DataTable
            columns={tableColumns}
            data={visibleData}
            loading={tableLoading}
            rowKey={r => String(r.id)}
            pagination={pagination}
            selectedRowKeys={selectedRowKeys}
            onSelectChange={onSelectChange}
            onRowClick={handleRowClick}
            onPageChange={handlePageChange}
            onTableChange={handleTableChange}
            className={'route-sheets-table'}
            headerRef={headerRef}
            editableFields={editableFields}
            onSaveEdit={onSaveEdit}
            maxLines={2}
            centerFooterContent={groupedFooterCenterContent}
            totals={totals}
            bordered
            highlightLastRowPrefix={'route-sheet'}
            rowClassName={record => (record.type === 'carrier-group' ? 'carrier-group-row' : '')}
          />
          <Modal
            title="Выбор даты для обновления"
            open={isModalImportVisible}
            onCancel={handleImportModalCancel}
            okButtonProps={{ loading: importLoading }}
            onOk={() => importForm.submit()}
          >
            <Form form={importForm} onFinish={handleImportData} layout="vertical">
              <Form.Item
                name="date"
                label="Дата поездки"
                rules={[{ required: true, message: 'Укажите дату поездки' }]}
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          </Modal>
          <RouteSheetsCardModal
            routeSheet={selectedRouteSheet}
            onClose={handleModalClose}
            onEditManual={handleOpenManualRouteSheetEdit}
          />
          <ManualRouteSheetModal
            open={manualRouteSheetModalOpen}
            routeSheet={manualRouteSheetForEdit}
            vehicles={availableFilters.vehicle || []}
            drivers={availableFilters.driver || []}
            centers={availableFilters.center || []}
            onClose={handleCloseManualRouteSheetModal}
            onSaved={handleManualRouteSheetSaved}
          />
        </>
      )}
    </B2BLayout>
  )
}

export default RouteSheetsList
